"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ServerToAdminEvent = exports.ServerWsCommandStatus = exports.AdminWsVerificationFailedAlert = exports.AdminWsAuditEventCreated = exports.AdminWsPlayerStatusUpdated = exports.AdminWsPlayerDown = exports.AdminWsPlayerOffline = exports.AdminWsPlayerOnline = exports.ServerToPlayerEvent = exports.ServerWsAssignmentUnavailable = exports.ServerWsCommandIssued = exports.ServerWsRevocationUpdated = exports.ServerWsAssignmentChanged = exports.PlayerToServerEvent = exports.PlayerWsVerificationFailed = exports.PlayerWsPlaybackEvent = exports.PlayerWsHeartbeat = void 0;
const zod_1 = require("zod");
const player_schema_js_1 = require("./player.schema.js");
// Client → server (PWA player → /ws/players)
exports.PlayerWsHeartbeat = zod_1.z.object({ type: zod_1.z.literal('heartbeat') }).merge(player_schema_js_1.HeartbeatInput);
exports.PlayerWsPlaybackEvent = zod_1.z
    .object({ type: zod_1.z.literal('playback.event') })
    .merge(player_schema_js_1.PlaybackEventsBatch);
exports.PlayerWsVerificationFailed = zod_1.z
    .object({ type: zod_1.z.literal('verification.failed') })
    .merge(player_schema_js_1.VerificationFailuresBatch);
exports.PlayerToServerEvent = zod_1.z.discriminatedUnion('type', [
    exports.PlayerWsHeartbeat,
    exports.PlayerWsPlaybackEvent,
    exports.PlayerWsVerificationFailed,
]);
// Server → client (server → PWA player)
exports.ServerWsAssignmentChanged = zod_1.z.object({
    type: zod_1.z.literal('assignment.changed'),
    composer_version_id: zod_1.z.string().uuid().nullable(),
});
exports.ServerWsRevocationUpdated = zod_1.z.object({
    type: zod_1.z.literal('revocation.updated'),
    list_version: zod_1.z.number().int().positive(),
});
exports.ServerWsCommandIssued = zod_1.z.object({
    type: zod_1.z.literal('command.issued'),
    command_id: zod_1.z.string().uuid(),
    // Detail is open-ended for P5+ (reboot/format-sd/standby/wake); P1 ignores.
    detail: zod_1.z.record(zod_1.z.unknown()),
    // P5 W2 §2.2 — Ed25519-signed envelope binding command_id / tenant_id /
    // player_id / command_type / args / issued_at / not_after. Verified by
    // the player against the cached signing-public-key set (same store
    // used for resource manifests, Phase-1 §4). canonical_json is the JCS
    // bytes the signature covers; the player re-canonicalizes and re-
    // verifies before dispatching the handler. signature_b64 is STANDARD
    // base64 (NOT base64url) per Phase-1 §7's content-signing convention.
    // P5 W2 T4 started emitting envelopes; T5 flips the field required and
    // wires the player-side verifier.
    envelope: zod_1.z.object({
        canonical_json: zod_1.z.string().min(1),
        signature_b64: zod_1.z.string().min(1),
        signing_key_id: zod_1.z.string().uuid(),
        not_after: zod_1.z.string().datetime(),
    }),
});
exports.ServerWsAssignmentUnavailable = zod_1.z.object({
    type: zod_1.z.literal('assignment.unavailable'),
    player_id: zod_1.z.string().uuid(),
    composer_id: zod_1.z.string().uuid(),
    reason: zod_1.z.literal('no_compatible_version'),
});
exports.ServerToPlayerEvent = zod_1.z.discriminatedUnion('type', [
    exports.ServerWsAssignmentChanged,
    exports.ServerWsRevocationUpdated,
    exports.ServerWsCommandIssued,
    exports.ServerWsAssignmentUnavailable,
]);
// Server → admin (/ws/admin)
exports.AdminWsPlayerOnline = zod_1.z.object({
    type: zod_1.z.literal('player.online'),
    player_id: zod_1.z.string().uuid(),
    at: zod_1.z.string().datetime(),
});
exports.AdminWsPlayerOffline = zod_1.z.object({
    type: zod_1.z.literal('player.offline'),
    player_id: zod_1.z.string().uuid(),
    at: zod_1.z.string().datetime(),
});
// SP2 screen inventory: an ACTIVE screen has had no heartbeat past PLAYER_DOWN_MS
// (~10 min) — a real outage, distinct from a brief offline blip. Pushed once per
// down episode by the stale-detector so the inventory surfaces "down" live.
exports.AdminWsPlayerDown = zod_1.z.object({
    type: zod_1.z.literal('player.down'),
    player_id: zod_1.z.string().uuid(),
    at: zod_1.z.string().datetime(),
});
exports.AdminWsPlayerStatusUpdated = zod_1.z.object({
    type: zod_1.z.literal('player.status.updated'),
    player_id: zod_1.z.string().uuid(),
    status: zod_1.z.record(zod_1.z.unknown()),
});
exports.AdminWsAuditEventCreated = zod_1.z.object({
    type: zod_1.z.literal('audit.event.created'),
    audit_event_id: zod_1.z.string().uuid(),
    resource_version_id: zod_1.z.string().uuid().nullable(),
    composer_version_id: zod_1.z.string().uuid().nullable(),
});
// Verification-failures last mile: dedicated admin alert emitted by the
// server on every persisted ingest batch (WsBroadcasterService.
// notifyVerificationFailedAlert). `failures` mirrors the ingest batch's
// VerificationFailure entries; kept as open records so the alert never
// fails to parse when the ingest contract gains fields.
exports.AdminWsVerificationFailedAlert = zod_1.z.object({
    type: zod_1.z.literal('verification.failed.alert'),
    player_id: zod_1.z.string().uuid(),
    failures: zod_1.z.array(zod_1.z.record(zod_1.z.unknown())),
    at: zod_1.z.string().datetime(),
});
// P5 W1: live ACK feed for the admin SPA's per-player command panel.
// Emitted by the server (a) when the command is issued (status='issued')
// and (b) when the player ACKs over the WS playback.event channel
// (status flips to one of ok/completed/failed/...).
exports.ServerWsCommandStatus = zod_1.z.object({
    type: zod_1.z.literal('command.status'),
    command_id: zod_1.z.string().uuid(),
    player_id: zod_1.z.string().uuid(),
    status: zod_1.z.enum([
        'issued',
        'ok',
        'failed',
        'unsupported',
        'stale',
        'rejected',
        'completed',
    ]),
    at: zod_1.z.string().datetime(),
    detail: zod_1.z.record(zod_1.z.unknown()).optional(),
});
exports.ServerToAdminEvent = zod_1.z.discriminatedUnion('type', [
    exports.AdminWsPlayerOnline,
    exports.AdminWsPlayerOffline,
    exports.AdminWsPlayerDown,
    exports.AdminWsPlayerStatusUpdated,
    exports.AdminWsAuditEventCreated,
    exports.AdminWsVerificationFailedAlert,
    exports.ServerWsCommandStatus,
]);
//# sourceMappingURL=ws-events.schema.js.map